import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AlertBoardView, AlertCategory } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { ScrollStrip } from '../ui/ScrollStrip';
import { StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { refreshUntilShown } from '../ui/QueryStates';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from '../tickets/sendOnce';
import { cn } from '@/lib/utils';
import {
  BAND_LABEL,
  BAND_STRIPE,
  BAND_TONE,
  ageLabel,
  subjectLine,
  untilLabel,
} from './alertsView';
import type { AlertCardData, AlertColumn } from './types';

/**
 * The board (spec §6, prototype 203 R): three columns, one per category, of cards sorted by
 * band then age (the server's order).
 *
 * ⭐ The columns scroll SIDEWAYS inside their own strip (`ScrollStrip`: a fade and a chevron at
 * an end with more past it), never the page; each column scrolls DOWN inside itself. On a
 * phone (below `md`; the board is the page's width, so a viewport breakpoint is the right
 * question here) one column shows at a time, picked by a segmented control: a pressed-button
 * group, each button `aria-controls` its column (LESSONS 2026-10-05: buttons that filter one
 * list are not tabs).
 *
 * ⚠️ THE CARD'S OPEN CONTROL AND ITS QUICK ACTIONS ARE SIBLINGS. A card that is one big
 * `role="button"` (the prototype's) hides the controls inside it from a screen reader and
 * swallows their clicks (memory `feedback_row_button_role_hides_its_controls`): the card is a
 * list item holding a button that opens the panel and, beside it, Take and Snooze.
 */
export function AlertBoard({
  columns,
  view,
  openId,
  phoneCol,
  mayWork,
  onOpen,
  onPickColumn,
  tz,
  now,
}: {
  columns: readonly AlertColumn[];
  view: AlertBoardView;
  /** The alert whose panel is open: its card is marked. */
  openId: string | undefined;
  /** The one column a phone shows. */
  phoneCol: AlertCategory;
  mayWork: boolean;
  onOpen: (alertId: string) => void;
  onPickColumn: (category: AlertCategory) => void;
  tz: string;
  now: number;
}) {
  return (
    <div className="space-y-3">
      <div
        role="group"
        aria-label="Column"
        data-probe="alert-columns-picker"
        className="grid grid-cols-3 gap-1 rounded-[var(--radius)] border border-border bg-surface p-1 md:hidden"
      >
        {columns.map((col) => (
          <button
            key={col.category}
            type="button"
            aria-pressed={col.category === phoneCol}
            aria-controls={`alert-column-${col.category}`}
            onClick={() => onPickColumn(col.category)}
            className="min-h-11 rounded-[var(--radius-sm)] px-1 py-1 text-xs font-medium text-muted-foreground aria-pressed:bg-[var(--sw-brand-pill)] aria-pressed:font-semibold aria-pressed:text-[var(--sw-brand-text)]"
          >
            <span className="block leading-tight">{col.label}</span>
            <span className="tabular-nums">{col.total}</span>
          </button>
        ))}
      </div>
      <ScrollStrip>
        <div className="flex gap-3 pb-2 md:min-w-max" data-probe="alert-board">
          {columns.map((col) => (
            <Column
              key={col.category}
              col={col}
              view={view}
              openId={openId}
              shownOnPhone={col.category === phoneCol}
              mayWork={mayWork}
              onOpen={onOpen}
              tz={tz}
              now={now}
            />
          ))}
        </div>
      </ScrollStrip>
    </div>
  );
}

function Column({
  col,
  view,
  openId,
  shownOnPhone,
  mayWork,
  onOpen,
  tz,
  now,
}: {
  col: AlertColumn;
  view: AlertBoardView;
  openId: string | undefined;
  shownOnPhone: boolean;
  mayWork: boolean;
  onOpen: (alertId: string) => void;
  tz: string;
  now: number;
}) {
  const headingId = `alert-heading-${col.category}`;
  const hidden = col.total - col.cards.length;
  const chips = (['critical', 'high', 'normal'] as const).filter((b) => col.bands[b] > 0);
  return (
    <section
      id={`alert-column-${col.category}`}
      aria-labelledby={headingId}
      data-column={col.category}
      className={cn(
        'flex w-full shrink-0 flex-col rounded-[var(--radius)] border border-border bg-surface md:flex md:w-[300px]',
        shownOnPhone ? '' : 'max-md:hidden',
      )}
    >
      <div className="space-y-1 border-b border-border px-3 py-2">
        <div className="flex items-baseline justify-between gap-2">
          <h2 id={headingId} className="text-sm font-semibold">
            {col.label}
          </h2>
          <span className="tabular-nums text-xs text-muted-foreground">{col.total}</span>
        </div>
        {chips.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {chips.map((b) => (
              <StatusBadge key={b} tone={BAND_TONE[b]}>
                {col.bands[b]} {BAND_LABEL[b].toLowerCase()}
              </StatusBadge>
            ))}
          </div>
        ) : null}
      </div>
      <ul className="max-h-[min(65dvh,44rem)] space-y-2 overflow-y-auto p-2">
        {col.cards.length === 0 ? (
          <li className="py-3 text-center text-xs text-muted-foreground">Nothing here</li>
        ) : (
          col.cards.map((a) => (
            <AlertBoardCard
              key={a.id}
              alert={a}
              view={view}
              open={a.id === openId}
              mayWork={mayWork}
              onOpen={onOpen}
              tz={tz}
              now={now}
            />
          ))
        )}
      </ul>
      {hidden > 0 ? (
        <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
          Showing the {col.cards.length} most urgent of {col.total}.
        </p>
      ) : null}
    </section>
  );
}

/**
 * One card: the band's stripe and chip, its age, what it is and what it is about, who is on it,
 * and, BESIDE the open control, Take (while nobody is) and Snooze (until tomorrow 7 AM; on the
 * Snoozed view, Unsnooze). A refusal (someone took it first) shows on the card.
 */
export function AlertBoardCard({
  alert: a,
  view,
  open,
  mayWork,
  onOpen,
  tz,
  now,
}: {
  alert: AlertCardData;
  view: AlertBoardView;
  open: boolean;
  mayWork: boolean;
  onOpen: (alertId: string) => void;
  tz: string;
  now: number;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const takeLock = useTapOnce();
  const snoozeLock = useTapOnce();
  const shows = trpc.alert.pathFilter();
  const take = useMutation(
    trpc.alert.take.mutationOptions({
      onSuccess: () => refreshUntilShown(qc, shows),
      // A refusal means the card was stale: read the board again (Codex r1).
      onError: () => refreshUntilShown(qc, shows),
      onSettled: () => takeLock.release(),
    }),
  );
  const snooze = useMutation(
    trpc.alert.snooze.mutationOptions({
      onSuccess: () => refreshUntilShown(qc, shows),
      // A refusal means the card was stale: read the board again (Codex r1).
      onError: () => refreshUntilShown(qc, shows),
      onSettled: () => snoozeLock.release(),
    }),
  );
  const unsnooze = useMutation(
    trpc.alert.unsnooze.mutationOptions({
      onSuccess: () => refreshUntilShown(qc, shows),
      // A refusal means the card was stale: read the board again (Codex r1).
      onError: () => refreshUntilShown(qc, shows),
      onSettled: () => snoozeLock.release(),
    }),
  );
  const subject = subjectLine(a, a.label);
  const titleId = `alert-${a.id}-title`;
  const error = take.error ?? snooze.error ?? unsnooze.error;
  const snoozedView = view === 'snoozed';

  return (
    <li
      data-alert-key={a.id}
      data-band={a.band}
      className={cn(
        'relative overflow-hidden rounded-[var(--radius-sm)] border text-sm',
        open ? 'border-brand bg-[var(--sw-brand-pill)]' : 'border-border bg-surface',
      )}
    >
      <span
        aria-hidden="true"
        className={cn('absolute inset-y-0 left-0 w-1', BAND_STRIPE[a.band])}
      />
      <button
        type="button"
        aria-haspopup="dialog"
        aria-current={open ? 'true' : undefined}
        aria-label={`${a.label}: ${subject}`}
        onClick={() => onOpen(a.id)}
        className="block w-full py-2 pl-3 pr-2 text-left hover:bg-surface-2"
      >
        <span className="flex items-center justify-between gap-2">
          <StatusBadge tone={BAND_TONE[a.band]}>{BAND_LABEL[a.band]}</StatusBadge>
          <span className="shrink-0 text-xs text-muted-foreground">
            {ageLabel(a.firstFiredAt, now)}
          </span>
        </span>
        <span id={titleId} className="mt-1 block font-medium">
          {a.label}
        </span>
        <span className="block break-words text-xs text-muted-foreground">{subject}</span>
        <span className="mt-1 block text-xs">
          {a.owner ? (
            <span className="font-medium">{a.owner.name ?? 'Former staff'}</span>
          ) : (
            <span className="text-muted-foreground">Nobody yet</span>
          )}
          {snoozedView && a.snoozedUntil ? (
            <span className="text-muted-foreground">
              {' '}
              · Snoozed until {untilLabel(a.snoozedUntil, tz)}
            </span>
          ) : null}
        </span>
      </button>
      {mayWork ? (
        <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
          {a.status === 'new' ? (
            <Button
              size="sm"
              variant="ghost"
              // A thumb's target on a phone (44 px); dense beside the column at a desk.
              className="max-md:min-h-11 max-md:px-3"
              aria-describedby={titleId}
              aria-disabled={take.isPending || undefined}
              onClick={() => sendOnce(takeLock, take.mutate, { alertId: a.id })}
            >
              Take
            </Button>
          ) : null}
          {snoozedView ? (
            <Button
              size="sm"
              variant="ghost"
              // A thumb's target on a phone (44 px); dense beside the column at a desk.
              className="max-md:min-h-11 max-md:px-3"
              aria-describedby={titleId}
              aria-disabled={unsnooze.isPending || undefined}
              onClick={() => sendOnce(snoozeLock, unsnooze.mutate, { alertId: a.id })}
            >
              Unsnooze
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              // A thumb's target on a phone (44 px); dense beside the column at a desk.
              className="max-md:min-h-11 max-md:px-3"
              aria-describedby={titleId}
              title="Snooze until tomorrow 7 AM"
              aria-disabled={snooze.isPending || undefined}
              onClick={() =>
                sendOnce(snoozeLock, snooze.mutate, {
                  alertId: a.id,
                  choice: 'tomorrow' as const,
                })
              }
            >
              Snooze
            </Button>
          )}
          {error ? (
            <p role="alert" className="w-full text-xs text-[var(--sw-danger-text)]">
              {error.message}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
